import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { UserEntrepriseAccesRow } from '@/lib/database.types';

// Sprint 8.0 — Gestion des accès multi-entreprises par user.
// La colonne users_metadata.entreprise_id reste l'entreprise principale ;
// cette table de jointure étend l'accès à N entreprises supplémentaires.

export function useUserEntreprises(userId: string | null) {
  return useQuery({
    queryKey: ['user_entreprises_acces', userId],
    enabled: !!userId,
    queryFn: async (): Promise<UserEntrepriseAccesRow[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('user_entreprises_acces')
        .select('*')
        .eq('user_id', userId);
      if (error) throw error;
      return (data ?? []) as unknown as UserEntrepriseAccesRow[];
    },
  });
}

export function useAllUserEntreprises() {
  return useQuery({
    queryKey: ['user_entreprises_acces_all'],
    queryFn: async (): Promise<UserEntrepriseAccesRow[]> => {
      const { data, error } = await supabase.from('user_entreprises_acces').select('*');
      if (error) throw error;
      return (data ?? []) as unknown as UserEntrepriseAccesRow[];
    },
  });
}

export interface SetUserEntreprisesInput {
  user_id: string;
  entreprises_ids: string[];
}

export function useSetUserEntreprises() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetUserEntreprisesInput): Promise<void> => {
      // Diff-based pour préserver granted_at/granted_by sur les lignes existantes.
      const { data: existing, error: errEx } = await supabase
        .from('user_entreprises_acces')
        .select('entreprise_id')
        .eq('user_id', input.user_id);
      if (errEx) throw errEx;

      const existingSet = new Set(
        (existing ?? []).map((r) => (r as { entreprise_id: string }).entreprise_id),
      );
      const desiredSet = new Set(input.entreprises_ids);

      const toAdd = [...desiredSet].filter((id) => !existingSet.has(id));
      const toRemove = [...existingSet].filter((id) => !desiredSet.has(id));

      if (toAdd.length > 0) {
        const { error } = await supabase.from('user_entreprises_acces').insert(
          toAdd.map((entreprise_id) => ({
            user_id: input.user_id,
            entreprise_id,
          })) as never,
        );
        if (error) throw error;
      }

      if (toRemove.length > 0) {
        const { error } = await supabase
          .from('user_entreprises_acces')
          .delete()
          .eq('user_id', input.user_id)
          .in('entreprise_id', toRemove);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user_entreprises_acces'] });
      void qc.invalidateQueries({ queryKey: ['user_entreprises_acces_all'] });
    },
  });
}
